import { useEffect, useState } from 'react';
import { dataService } from '@/services/dataService';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { EntityDrawer } from '@/components/ui/EntityDrawer';
import { useI18n } from '@/i18n';
import { useToast } from '@/components/ui/Toast';

type ConnectorRow = { key: string; displayName: string; category: string; status: string; label?: string; enabledUi: boolean; notes?: string };

export function ConnectorsPage() {
  const { t } = useI18n();
  const push = useToast();
  const [rows, setRows] = useState<ConnectorRow[]>([]);
  const [selected, setSelected] = useState<ConnectorRow | null>(null);
  const [labelDraft, setLabelDraft] = useState('');
  const [notesDraft, setNotesDraft] = useState('');

  function reload() {
    dataService.listConnectorStatuses().then(setRows);
  }
  useEffect(reload, []);

  function openDetails(r: ConnectorRow) {
    setSelected(r);
    setLabelDraft(r.label ?? r.displayName);
    setNotesDraft(r.notes ?? '');
  }

  async function saveDetails() {
    if (!selected) return;
    await dataService.updateConnectorOverride(selected.key, { label: labelDraft, notes: notesDraft });
    push(t('toast.settingsSaved'), 'success');
    setSelected(null);
    reload();
  }

  async function toggleEnabled(r: ConnectorRow) {
    await dataService.updateConnectorOverride(r.key, { enabledUi: !r.enabledUi });
    reload();
  }

  return (
    <div>
      <h1>{t('nav.connectors')}</h1>
      <p style={{ fontSize: 13, color: 'var(--text-2)', marginTop: -8 }}>{t('connectors.honestyNote')}</p>
      <div className="card" style={{ padding: 8 }}>
        <div className="table-scroll">
        <table className="dt">
          <thead><tr><th>Connector</th><th>Category</th><th>Status</th><th>UI</th><th></th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key}>
                <td>{r.label || r.displayName}</td>
                <td>{r.category}</td>
                <td><StatusBadge entity="connector" statusKey={r.status} /></td>
                <td><span className={`badge ${r.enabledUi ? 'good' : 'neutral'}`}>{r.enabledUi ? t('connectors.enabled') : t('connectors.disabled')}</span></td>
                <td style={{ display: 'flex', gap: 6 }}>
                  <button onClick={() => openDetails(r)}>{t('connectors.viewDetails')}</button>
                  <button onClick={() => toggleEnabled(r)}>{r.enabledUi ? t('connectors.disable') : t('connectors.enable')}</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      {selected && (
        <EntityDrawer title={selected.displayName} subtitle={selected.category} onClose={() => setSelected(null)}>
          <p><b>{t('connectors.status')}:</b> <StatusBadge entity="connector" statusKey={selected.status} /></p>
          <label style={{ display: 'block', fontSize: 12, marginTop: 10, marginBottom: 4 }}>{t('connectors.editLabel')}</label>
          <input className="text-input" value={labelDraft} onChange={(e) => setLabelDraft(e.target.value)} style={{ marginBottom: 10 }} />
          <label style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>{t('connectors.notes')}</label>
          <textarea className="text-input" rows={3} value={notesDraft} onChange={(e) => setNotesDraft(e.target.value)} style={{ marginBottom: 12 }} />
          <div style={{ fontSize: 12, color: 'var(--warn)', marginBottom: 12 }}>{t('connectors.requiresLiveIntegration')}</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn-primary" onClick={saveDetails}>{t('common.save')}</button>
            <button onClick={() => setSelected(null)}>{t('common.cancel')}</button>
          </div>
        </EntityDrawer>
      )}
    </div>
  );
}
